import { createReducer, WritableDraft } from "@reduxjs/toolkit";
import { APIModule } from "../../../server/types/index.js";
import {
  ActionModule,
  ActionModules,
  Language,
  ModuleType,
} from "../../types/index.js";
import { keys } from "../../utils/utils.js";
import { deleteModuleTranslationStart } from "./actions.js";
import { getActionModules, postModule } from "./thunks.js";

export const initialState: ActionModules = {};

const sortOrder: { [K in ModuleType]?: number } = {
  BookingModule: 1,
  EnquiryModule: 2,
  RoomsModule: 3,
  SpecialsModule: 4,
};

const addActionModule = (
  state: WritableDraft<ActionModules>,
  languageIds: Language[],
  actionModule: ActionModule,
) => {
  if (!sortOrder[actionModule.type]) return;

  languageIds.forEach((languageId) => {
    state[languageId] = sortActionModules(state[languageId] ?? []);
  });
};

const sortActionModules = (actionModules: ActionModule[]) => {
  return [...actionModules].sort(
    ({ type: moduleType1 }, { type: moduleType2 }) => {
      const v1 = sortOrder[moduleType1] || 5;
      const v2 = sortOrder[moduleType2] || 5;

      if (v1 < v2) return -1;
      else if (v1 === v2) return 0;
      return 1;
    },
  );
};

const deleteActionModule = (
  state: WritableDraft<ActionModules>,
  moduleId: string,
  deleteAllTranslations: boolean,
  languageId: Language,
) => {
  if (deleteAllTranslations) {
    return keys(state).forEach((languageId) => {
      const current = state[languageId] || [];
      state[languageId] = current.filter(({ id }) => moduleId !== id);
    });
  }

  const filtered = (state[languageId] || []).filter(
    ({ id }) => moduleId !== id,
  );

  state[languageId] = filtered;
};

const getActionModulesInternal = (modules: APIModule[]): ActionModules => {
  const actionModules = modules.reduce<ActionModules>(
    (carry, { id, pageId, type, translations }) => {
      if (!pageId) return carry;

      const actionModule: ActionModule = { id, pageId, type };

      const updatedState = keys(translations).reduce<ActionModules>(
        (innerCarry, languageId) => {
          const current = [...(innerCarry[languageId] || []), actionModule];
          return { ...innerCarry, [languageId]: current };
        },
        carry,
      );

      return updatedState;
    },
    {},
  );

  const sortedState = keys(actionModules).reduce<ActionModules>(
    (carry, languageId) => {
      const current = carry[languageId] || [];
      const sorted = sortActionModules(current);
      return { ...carry, [languageId]: sorted };
    },
    actionModules,
  );

  return sortedState;
};

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(postModule.pending, (state, action) => {
    const { moduleId, moduleType, pageId, languageIds } = action.meta.arg;
    if (pageId === null) {
      return state;
    }

    return addActionModule(state, languageIds, {
      id: moduleId,
      pageId,
      type: moduleType,
    });
  });

  builder.addCase(getActionModules.fulfilled, (_state, action) =>
    getActionModulesInternal(action.payload),
  );

  builder.addCase(deleteModuleTranslationStart, (state, action) => {
    const { moduleId, languageId, deleteAllTranslations } = action.payload;
    deleteActionModule(state, moduleId, deleteAllTranslations, languageId);
  });
});

export default reducer;
